import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from 'components/UI/Dialog';
import LoadingSpinner from 'components/UI/LoadingIndicators/LoadingSpinner';
import { connect } from 'react-redux';
import {
    buildHotelsSelector,
} from 'store/selectors/hotels.selector';
import EditableSelect from 'components/UI/Input/EditableSelect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faAsterisk, faCheckSquare, faSquare,
} from '@fortawesome/free-solid-svg-icons';
import currencyFormat from 'utils/currency-format';
import Table from 'components/UI/Table';
import Button from 'components/UI/Button';
import Col from 'components/UI/Layout/Col';
import ServiceComponentIcon from 'components/UI/ServiceComponentIcon';

function AttachServiceModal({
    suggestedUsers, onSave, hotels, showAttachServiceModal, setShowAttachServiceModal, loading, sendEmail, isForReservation,
})
{
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState({});
    const hotelOptions = hotels.map((hotel) => ({ hotel, label: hotel.name, value: hotel.id }));
    const [activeHotel, setActiveHotel] = useState(null);
    const [haveServiceSuggested, setHaveServiceSuggested] = useState(false);

    useEffect(() =>
    {
        if (hotelOptions.length === 1)
        {
            setActiveHotel(hotelOptions[0]);
            handleHotelChange(hotelOptions[0]);
        }
    }, [hotels]);

    const toggleService = (serviceId) =>
    {
        setSelectedService({ ...selectedService, [serviceId]: !selectedService[serviceId] });
    };

    const handleHotelChange = (hotelValue) =>
    {
        if (!hotelValue?.hotel) return;
        const activeHotelServices = hotelValue.hotel.defaultServices.map((service) =>
        {
            const suggestedPeople = service.amount_of_people - suggestedUsers;

            if (suggestedPeople < 0)
            {
                return null;
            }

            return { ...service, suggested: suggestedPeople };
        }).filter((n) => n).sort((a, b) =>
        {
            if (a.suggested < b.suggested)
            {
                return -1;
            }
            if (a.suggested > b.suggested)
            {
                return 1;
            }
            return 0;
        });

        setActiveHotel(hotelValue);
        setServices(activeHotelServices);
    };

    useEffect(() =>
    {
        if (hotels.length === 1)
        {
            handleHotelChange(
                { hotel: hotels[0], label: hotels[0].name, value: hotels[0].id },
            );
        }
    }, [hotels]);

    useEffect(() =>
    {
        setSelectedService({});
    }, [activeHotel]);

    useEffect(() =>
    {
        if (services.length > 0)
        {
            setHaveServiceSuggested(
                services.reduce((haveService, service) => haveService || service.suggested > 0, false)
            );
        }
    }, [services]);

    useEffect(() =>
    {
        if (!showAttachServiceModal)
        {
            setActiveHotel(null);
        }
    }, [showAttachServiceModal]);

    return (
        <Dialog
            fullSize
            model={{ value: showAttachServiceModal, setter: setShowAttachServiceModal }}
            footer={(
                <>
                    <button
                        onClick={() => onSave(activeHotel?.hotel?.id, selectedService)}
                        disabled={loading}
                        type="button"
                        className="tw-text-white tw-mt-3 tw-inline-flex tw-w-full tw-justify-center tw-rounded-md tw-border tw-bg-primary-default tw-px-4 tw-pt-2 tw-pb-1.5 tw-items-center tw-text-base tw-font-medium tw-shadow-sm hover:tw-bg-primary-hover focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-ring-offset-2 sm:tw-mt-0 sm:tw-ml-3 sm:tw-w-auto sm:tw-text-sm"
                    >
                        Confirm <LoadingSpinner loading={loading} className="tw-mr-1 tw-ml-2" />
                    </button>
                    <button
                        disabled={loading}
                        onClick={() =>
                        {
                            setShowAttachServiceModal(null);
                        }}
                        type="button"
                        className="tw-mt-3 tw-inline-flex tw-w-full tw-justify-center tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-pt-2 tw-pb-1.5 tw-items-center tw-text-base tw-font-medium tw-text-gray-700 tw-shadow-sm hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-ring-offset-2 sm:tw-mt-0 sm:tw-ml-3 sm:tw-w-auto sm:tw-text-sm"
                    >
                        Close
                    </button>
                </>
            )}
        >
            <div>
                <h3 className="tw-text-lg tw-font-medium tw-leading-6 tw-text-gray-900 dark:tw-text-gray-100">
                    Select Hotel
                </h3>

                <EditableSelect
                    editing
                    labelText={false}
                    disableLabel
                    inputProps={{
                        placeholder: 'Select Hotel',
                        getOptionLabel: (option) => option?.label,
                        getOptionValue: (option) => option.value,
                        options: hotelOptions,
                        value: activeHotel,
                        onChange: (value) => handleHotelChange(value),
                    }}
                />

                {activeHotel
                && (
                    <>
                        <h3 className="tw-text-lg tw-font-medium tw-leading-6 tw-text-gray-900 dark:tw-text-gray-100 tw-mt-3">
                            Select Services to Attach
                        </h3>

                        <Table
                            thClass="tw-text-left"
                            data={services.map((service) => ({
                                id: service.id,
                                Select: <Button customBg="tw-text-primary-default" type="custom" margin="tw-mt-0" onClick={() => toggleService(service.id)}><FontAwesomeIcon size="lg" icon={selectedService[service.id] ? faCheckSquare : faSquare} /></Button>,
                                Service: <><ServiceComponentIcon className="tw-mr-3" service={service.type} /> {service.name}</>,
                                Price: currencyFormat(service.default_rack),
                                'Max Passengers': <>{ service.amount_of_people }</>,
                            }))}
                            trClassName="tw-pointer tw-text-left"
                            tdGeneralClassName="tw-text-left"
                            tdClassName={{
                                Service: 'tw-whitespace-nowrap !tw-text-left',
                                'Max Passengers': 'tw-text-center tw-pr-10',
                            }}
                            headers={['Select', 'Service', 'Max Passengers', 'Price']}
                            containerClass="tw-px-0 tw-max-h-96 tw-mb-3"
                        />

                        <Col className="tw-text-center tw-mt-3 tw-mb-0">
                            <p className="tw-my-0">
                                Need another type of service?
                            </p>
                            <Button margin="tw-my-0" onClick={sendEmail}>Contact your Travel Advisor</Button>
                        </Col>
                    </>
                )}

            </div>
        </Dialog>
    );
}

AttachServiceModal.defaultProps = {
    isForReservation: false,
};

AttachServiceModal.propTypes = {
    onSave: PropTypes.func,
    showAttachServiceModal: PropTypes.bool,
    setShowAttachServiceModal: PropTypes.func,
    sendEmail: PropTypes.func,
    suggestedUsers: PropTypes.number,
    hotels: PropTypes.any,
    loading: PropTypes.bool,
    isForReservation: PropTypes.bool,
};

const mapStateToProps = () =>
{
    const hotelsSelector = buildHotelsSelector();

    return (state, props) => ({
        hotels: hotelsSelector(state, props.hotelIds),
    });
};

export default connect(mapStateToProps)(AttachServiceModal);
